import type { ITabItem } from 'components/Form/ControlledTab';
import type {
    BimContractConstructionTypeData,
    BimWorkOrderVersionListItem,
    CalculationNavData,
    ConstructionTypeData,
    WorkOrderVersionListItem,
} from 'gql/graphql';
import {
    BimTotal_AlcTypeEnum as BimTotalAlcTypeEnum,
    ChangeNoticeFlags,
    ContractResultEnum,
    ProjectContractTypeEnum,
} from 'gql/graphql';
import { formatDateToLocale, formatNumberToRu } from 'utils';

import type { ValueFormatterParams } from 'ag-grid-community/dist/types/core/entities/colDef';
import { isNil } from 'ramda';

import { NavItems } from './ProjectTree/constants';
import { TabsIds } from './types';

export interface IConstructionTypeListItem {
    id: number;
    label: string;
    code: string;
    index?: number;
    disabled?: boolean;
    disabledMessage?: string;
}

export const contractTabs: ITabItem<TabsIds>[] = [
    { id: TabsIds.COMMON, label: 'Общие данные' },
    { id: TabsIds.PRICE, label: 'Ценовые параметры' },
    { id: TabsIds.ACCUMULATION_SHEET, label: 'Накопительная ведомость' },
];

export const projectContractTypesTitles = {
    [ProjectContractTypeEnum.Frame]: 'Рамочный',
    [ProjectContractTypeEnum.Normal]: 'Обычный',
};

export const getProjectContractTypeTitle = (type?: ProjectContractTypeEnum) =>
    projectContractTypesTitles[type as ProjectContractTypeEnum] ||
    projectContractTypesTitles[ProjectContractTypeEnum.Normal];

export const getConstructionTypeListItem = (
    t: ConstructionTypeData | BimContractConstructionTypeData,
    index?: number,
): IConstructionTypeListItem => ({
    id: t.id,
    label: t.name,
    code: t.code,
    index,
});

export const allConstructionTypes: Record<string, string> = {
    '-9': 'Куст',
    '-8': 'Площадка',
    '-7': 'Газопровод',
    '-6': 'Инженерная подготовка',
    '-5': 'Автомобильные дороги',
    '-4': 'Линии электропередачи',
    '-3': 'Водовод',
    '-2': 'Нефтесбор',
};

export const messageForObjectConstructionTypes =
    'Имеются объекты по виду строительства на странице "Структура расчета". Удаление недоступно';
export const messageForOthersConstructionTypes =
    'Имеются прочие по виду строительства на странице "Доп.транспорт и лимитированные затраты"/"Прочие затраты". Удаление недоступно';

export interface IWorkOrderVersionListItem {
    id: number;
    label: string;
    labelWithoutChangeNumber: string;
    item: WorkOrderVersionListItem | BimWorkOrderVersionListItem;
}

export const getWorkOrderVersionListItems = (
    items?: WorkOrderVersionListItem[] | BimWorkOrderVersionListItem[],
): IWorkOrderVersionListItem[] =>
    (items as any[])?.reduce(
        (acc: IWorkOrderVersionListItem[], item: WorkOrderVersionListItem | BimWorkOrderVersionListItem) => {
            const workOrderNumber = item.workOrderVersionItem.internalNumber;
            const workOrderDate = formatDateToLocale(item.workOrderVersionItem.signingDate);
            const workOrderChangeNumber = item?.workOrderChangeItem?.internalNumber;
            const workOrderChangeDate = formatDateToLocale(item?.workOrderChangeItem?.signingDate);

            const labelFull = `НЗ №${workOrderNumber} Изм. №${workOrderChangeNumber} от ${workOrderChangeDate}`;
            const labelWithoutChangeNumber = `НЗ №${workOrderNumber} от ${workOrderChangeDate}`;
            const labelCut = `НЗ №${workOrderNumber} от ${workOrderDate}`;

            acc.push({
                id: item.workOrderVersionItem.id,
                label: item.workOrderChangeItem ? labelFull : labelCut,
                labelWithoutChangeNumber: item.workOrderChangeItem ? labelWithoutChangeNumber : labelCut,
                item,
            });

            return acc;
        },
        [],
    ) || [];

const placeholder = '—';

export const getCurrentCostRounding = (
    dataType: ContractResultEnum | BimTotalAlcTypeEnum | undefined | null,
    decimal: number,
) =>
    dataType &&
    [
        ContractResultEnum.Gross,
        ContractResultEnum.Tax,
        BimTotalAlcTypeEnum.Tax,
        BimTotalAlcTypeEnum.TotalWithTax,
    ].includes(dataType)
        ? 2
        : decimal;

export const numberFormatterWithLinePlaceholder = (
    { value, dataType }: { value: number; dataType?: ContractResultEnum | undefined | null },
    decimal = 2,
) => {
    const currentCostRounding = getCurrentCostRounding(dataType, decimal);

    return isNil(value) ? placeholder : formatNumberToRu(value, currentCostRounding);
};

export const getNumberFormatterWithLinePlaceholder =
    (decimal: number) =>
    (params: ValueFormatterParams<{ type?: ContractResultEnum; totalType?: BimTotalAlcTypeEnum }>) => {
        const { value, data } = params ?? {};

        const currentCostRounding = getCurrentCostRounding(data?.type ?? data?.totalType, decimal);

        return numberFormatterWithLinePlaceholder({ value }, currentCostRounding);
    };

export const isCostRoundingOrSeasonsChanged = (navLinkId: NavItems, calculationNavData?: CalculationNavData) => {
    const metadata = calculationNavData?.metadata ?? {};
    const changeNotices = metadata?.changeNotices ?? '';

    switch (navLinkId) {
        case NavItems.CalculationOther:
            return {
                isSeasonsChanged: metadata?.hasOtherCosts && changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange),
                isCostRoundingChanged:
                    metadata?.hasOtherCosts && changeNotices.includes(ChangeNoticeFlags.OnCostRoundingChange),
            };

        case NavItems.CalculationMtrs:
            return {
                isSeasonsChanged: metadata?.hasMtrItems && changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange),
                isCostRoundingChanged: false,
            };

        case NavItems.CalculationVor:
            return {
                isSeasonsChanged: metadata?.hasWORItems && changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange),
                isCostRoundingChanged: false,
            };

        case NavItems.CalculationLimitedCosts:
            return {
                isSeasonsChanged:
                    metadata?.hasLimitedCosts &&
                    changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange) &&
                    metadata?.lastComputedAt !== null,
                isCostRoundingChanged: false,
            };

        case NavItems.CalculationStructure:
            return {
                isSeasonsChanged: changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange),
                isObjectStructureChanged: changeNotices.includes(ChangeNoticeFlags.OnObjectStructureChange),
            };

        case NavItems.CalculationPreview:
            return {
                isSeasonsChanged:
                    metadata?.lastComputedAt !== null && changeNotices.includes(ChangeNoticeFlags.OnSeasonsChange),
                isCostRoundingChanged: false,
            };

        default:
            return {
                isSeasonsChanged: false,
                isCostRoundingChanged: false,
            };
    }
};

export const getNumberFormatter =
    (decimal: number, maxDigits?: number) =>
    ({ value }: { value: number }) =>
        formatNumberToRu(value, decimal, maxDigits);

export const costRoundingItems = [
    { id: 2, label: 'Два знака после запятой' },
    { id: 0, label: 'Целое число' },
];
